import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { DialogCloseEvent } from '@theme/dialog';
import { formatMoney, fetchPriceData } from '@theme/theme-currency';
import { localizeUrl } from '@theme/localize-url';

const searchHistoryKey = 'search_history';
const maxHistoryTerms = 5;
const minSearchLength = 3;

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton', 'form'];

  #controller = new AbortController();
  #pendingRequest = null;

  #pageLoadId = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
  #activeIndex = -1;

  #debouncedSearch = debounce((searchTerm) => this.#fetchAndRender(searchTerm), 300);
  #debouncedReset = debounce((keepFocus) => {
    if (keepFocus && this.refs.searchInput) this.refs.searchInput.focus();
    this.#resetSearch();
  }, 100);

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    this.STOREFRONT_TOKEN = Theme.otterVars?.storefrontToken || "";
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const apiDomain = isLocal ? (window.Shopify?.shop || 'your-store.myshopify.com') : window.location.hostname;
    this.GRAPHQL_URL = `https://${apiDomain}/api/2024-01/graphql.json`;
    this.currencySettings = {
      currencyCodeEnabled: window.OtterVars.MarketSettings?.currencyCodeEnabled,
      moneyWithCurrencyFormat: window.OtterVars.MarketSettings?.moneyWithCurrencyFormat
    };
    this.searchTrendingTag = window.OtterVars.MarketSettings?.searchTrendingTag;
    this.siteId = window.OtterVars.searchspringId;

    this.#initTemplates();
    this.#setupEventListeners();
    this.#saveSearchFromUrl();

    onDocumentLoaded(() => this.resetSearch(false));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#pendingRequest?.abort();
  }

  #initTemplates() {
    this.cardTemplate = document.getElementById('searchspring-product-card-template');
    this.noResultsTemplate = document.getElementById('search-no-search-term');
    this.searchResultsTemplate = document.getElementById('search-results');
  }

  #setupEventListeners() {
    const { signal } = this.#controller;
    const { searchInput, form, predictiveSearchResults } = this.refs;

    if (searchInput.value.length > 0) this.#toggleResetButton(true);

    // Setup ARIA attributes for accessibility
    searchInput.setAttribute('role', 'combobox');
    searchInput.setAttribute('aria-autocomplete', 'list');
    searchInput.setAttribute('aria-expanded', 'false');
    if (predictiveSearchResults) {
      const resultsId = predictiveSearchResults.id || 'predictive-search-results';
      predictiveSearchResults.id = resultsId;
      searchInput.setAttribute('aria-controls', resultsId);
    }

    // Accessibility: keyboard navigation
    searchInput.addEventListener('keydown', this.#handleKeyboardNavigation, { signal });

    if (this.dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      this.dialog.addEventListener(DialogCloseEvent.eventName, () => this.resetSearch(false), { signal });
      this.addEventListener('click', this.#handleModalFocus, { signal });
    }

    form.addEventListener('submit', () => {
      const term = searchInput.value.trim();
      if (term && this.results?.results?.length > 0) {
        this.#saveSearchTerm(term);
      }
    });
  }

  search(event) {
    if (!event.inputType) return;

    const input = this.refs.searchInput;
    if (!input) return;

    const searchTerm = input.value.trim();

    // Reset keyboard navigation
    this.#activeIndex = -1;

    if (!searchTerm.length) {
      this.#pendingRequest?.abort();
      this.resetSearch(false);
      return;
    }

    this.#toggleResetButton(true);

    if (searchTerm.length >= minSearchLength) {
      this.#debouncedSearch(searchTerm);
    }
  }

  resetSearch(keepFocus = true) {
    this.#debouncedReset(keepFocus);
  }

  async #fetchAndRender(searchTerm) {
    if (!this.siteId) return;

    this.refs.form.classList.remove('predictive-search-form--loaded');

    this.#pendingRequest?.abort();
    this.#pendingRequest = new AbortController();
    const signal = this.#pendingRequest.signal;

    try {
      const results = await this.apiRequest('search', searchTerm, signal);
      if (signal.aborted) return;

      if (results?.results?.length) {
        const variantGids = results.results.map(product => {
          let allSwatches = [];
          try {
            allSwatches = product.ss_swatches ? JSON.parse(product.ss_swatches.replace(/&quot;/g, '"')) : [];
          } catch (e) {
            console.error("Predictive Search: Swatch parse error", e);
          }

          const matchingSwatch = allSwatches.find(s => s.sku?.toLowerCase() === searchTerm.toLowerCase());
          if (matchingSwatch) {
            product.matchedSwatch = matchingSwatch;
          }

          const initialSwatch = matchingSwatch || allSwatches.find(s => s.available > 0) || {};
          let vId = initialSwatch.url
            ? new URL(initialSwatch.url, window.location.origin).searchParams.get('variant')
            : product.variant_id;

          if (typeof vId === 'string' && vId.includes(',')) {
            vId = vId.split(',')[0].trim();
          } else if (Array.isArray(vId)) {
            vId = vId[0]; // Defensive check for arrays
          }

          return `gid://shopify/ProductVariant/${vId}`;
        });

        const { ok: priceLookupConfirmed, nodes: livePriceNodes, error: priceLookupError } = await fetchPriceData(variantGids, {
          GRAPHQL_URL: this.GRAPHQL_URL,
          STOREFRONT_TOKEN: this.STOREFRONT_TOKEN
        });

        if (!priceLookupConfirmed) {
          console.warn('Predictive Search: Shopify price lookup failed or was incomplete; using Searchspring data for unresolved products.', priceLookupError);
        }

        // Map by node.id to prevent misalignment from null results
        const priceMap = new Map();
        livePriceNodes.forEach((node) => {
          if (node && node.id) {
            priceMap.set(node.id, node);
          }
        });

        // Filter out unavailable products and attach live pricing
        results.results = results.results.filter((product, index) => {
          const vId = variantGids[index];
          const liveNode = priceMap.get(vId);

          // A missing node only means "gone" when Shopify confirmed the lookup; otherwise trust Searchspring.
          const is_product_available = (product.ss_available === '0' || (!liveNode && priceLookupConfirmed) || liveNode?.availableForSale === false) ? false : true;

          if (!is_product_available) {
            return false; // Skip this product completely
          }

          if (!liveNode) return true;

          // Attach live prices so the template renderer can use them
          product.livePrice = liveNode.price?.amount;
          product.liveCompareAt = liveNode.compareAtPrice?.amount;

          return true; // Keep product in the results
        });
      }

      let suggestions = null;
      if (results?.results?.length) {
        suggestions = await this.apiRequest('suggestions', searchTerm, signal);
      }

      this.results = results;
      this.#renderResults(searchTerm, results, suggestions);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error("Predictive Search Error:", err);
    } finally {
      if (!signal.aborted) {
        this.refs.form.classList.add('predictive-search-form--loaded');
      }
    }
  }

  async apiRequest(type, searchTerm = '', signal = null) {
    if (!this.siteId) return null;

    const baseUrl = `https://${this.siteId}.a.searchspring.io`;

    const getCommonParams = () => ({
      siteId: this.siteId ?? '',
      resultsFormat: 'json',
      domain: window.location.href,
      resultsPerPage: '6',
      page: '1',
      redirectResponse: 'direct',
      source: 'input',
      userId: this.#getOrSetCookie('ssUserId'),
      sessionId: this.#getOrSetCookie('ssSessionId'),
      pageLoadId: this.#pageLoadId
    });

    let url;

    switch (type) {
      case 'search': {
        const params = new URLSearchParams({
          ...getCommonParams(),
          q: searchTerm
        });
        url = `${baseUrl}/api/search/autocomplete.json?${params.toString()}`;
        break;
      }
      case 'suggestions': {
        const params = new URLSearchParams({
          siteId: this.siteId,
          q: searchTerm,
          suggestionCount: '5'
        });
        url = `${baseUrl}/api/suggest/query?${params.toString()}`;
        break;
      }
      case 'trending': {
        const params = new URLSearchParams({
          siteId: this.siteId,
          limit: '5'
        });
        url = `${baseUrl}/api/suggest/trending?${params.toString()}`;
        break;
      }
      case 'boost': {
        const params = new URLSearchParams({
          tags: this.searchTrendingTag,
          limits: '6'
        });
        url = `${baseUrl}/boost/${this.siteId}/recommend?${params.toString()}`;
        break;
      }
      default:
        return null;
    }

    const response = await fetch(url, {
      signal: signal || this.#controller.signal,
      headers: { accept: 'application/json' }
    });

    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.json();
  }

  #renderResults(searchTerm, data, suggestions) {
    const { predictiveSearchResults, searchInput } = this.refs;

    if (!predictiveSearchResults || !this.searchResultsTemplate) return;

    const container = predictiveSearchResults.parentNode;

    if (!data?.results?.length) {
      this.#resetSearch(true);
      return;
    }

    container?.classList.remove('predictive-search--no-results');
    searchInput.setAttribute('aria-expanded', 'true');

    predictiveSearchResults.innerHTML = this.searchResultsTemplate.innerHTML;

    const productsList = predictiveSearchResults.querySelector('.js-search-results-products');
    if (productsList) {
      productsList.replaceChildren(this.#generateProductsHtml(data.results));
    }

    // Update results count
    const resultsCount = predictiveSearchResults.querySelector('.predictive-search-results__count');
    if (resultsCount && data?.results?.length) {
      const count = data.results.length;

      let countText;
      if (count) {
        countText = `${count} ${count === 1 ? 'result' : 'results'}`;
      }

      resultsCount.textContent = countText;
    }

    const suggestionsContainer = predictiveSearchResults.querySelector('#suggestions');
    if (suggestionsContainer && suggestions?.alternatives) {
      suggestionsContainer.innerHTML = suggestions.alternatives
        .map(s => this.#renderSuggestionItem(s.text, searchTerm))
        .join('');
    }

    const viewAllBtn = predictiveSearchResults.querySelector('.predictive-search__search-button');
    if (viewAllBtn) viewAllBtn.href = localizeUrl(`/search?q=${encodeURIComponent(searchTerm)}`);
  }

  #renderSuggestionItem(text, highlight) {
    const li = document.createElement('li');
    li.className = 'predictive-search-results__item';
    li.setAttribute('role', 'option');

    const a = document.createElement('a');
    a.href = localizeUrl(`/search?q=${encodeURIComponent(text)}`);
    a.className = 'predictive-search-results__link';

    const span = document.createElement('span');
    span.className = 'predictive-search-results__term';

    if (highlight) {
      const escaped = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escaped})`, 'gi');

      const parts = text.split(regex);

      parts.forEach(part => {
        if (part.toLowerCase() === highlight.toLowerCase()) {
          const mark = document.createElement('mark');
          mark.classList.add('ss-highlight');
          mark.textContent = part;
          span.appendChild(mark);
        } else {
          span.appendChild(document.createTextNode(part));
        }
      });
    } else {
      span.textContent = text;
    }

    a.appendChild(span);
    li.appendChild(a);

    return li.outerHTML;
  }

  #generateProductsHtml(products, isTrending = false) {
    const list = document.createElement('ul');
    list.className = 'predictive-search-results__list predictive-search-results__wrapper-products list-unstyled';
    list.setAttribute('role', 'listbox');

    products.forEach(product => {
      const clone = this.cardTemplate.content.cloneNode(true);
      this.#fillProductTemplate(clone, product, isTrending);

      const listItem = clone.querySelector('li');
      if (listItem) {
        listItem.setAttribute('role', 'option');
      }

      list.appendChild(clone);
    });
    return list;
  }

  #fillProductTemplate(clone, product, isTrending) {
    if (isTrending) {
      product = product.mappings.core;
    }

    let productUrl = product.matchedSwatch?.url || product.url;
    let productTitle = product.name;
    let imageSrc = product.matchedSwatch?.image || product.imageUrl || product.thumbnailImageUrl;

    clone.querySelectorAll('a').forEach(link => link.href = localizeUrl(productUrl));

    const card = clone.querySelector('.resource-card');
    const cardLink = card.querySelector('.resource-card__link');
    if (card) card.setAttribute('href', localizeUrl(productUrl));
    if (cardLink) cardLink.setAttribute('href', localizeUrl(productUrl));

    const title = clone.querySelector('.resource-card__title');
    if (title) title.textContent = productTitle;

    const titleSr = clone.querySelector('.resource-card__title-sr');
    if (titleSr) titleSr.textContent = productTitle;

    const img = clone.querySelector('.resource-card__image');
    if (img instanceof HTMLImageElement) {
      let imageAlt = productTitle || product.title;

      if (imageSrc === null || imageSrc === undefined) {
        imageSrc = img.getAttribute('data-placeholder-src');
        imageAlt = img.getAttribute('data-placeholder-alt') || '';
      }

      img.src = imageSrc;
      img.alt = imageAlt;
    }

    const priceContainer = clone.querySelector('.price-container');

    if (priceContainer) {
      const currentPrice = product.livePrice || product.price;
      const comparePrice = product.liveCompareAt || product.msrp;

      const priceFormatted = formatMoney(currentPrice, this.currencySettings);
      let priceMarkup = `<span class="price">${priceFormatted}</span>`;

      if (comparePrice && parseFloat(comparePrice) > parseFloat(currentPrice)) {
        const comparePriceFormatted = formatMoney(comparePrice, this.currencySettings);
        priceMarkup = `<span class="compare-at-price">${comparePriceFormatted}</span> ${priceMarkup}`;
      }

      priceContainer.innerHTML = priceMarkup;
    }
  }

  #resetSearch = async (noResults = false) => {
    const { predictiveSearchResults, searchInput, form } = this.refs;
    if (!this.noResultsTemplate) return;

    // Reset keyboard navigation
    this.#activeIndex = -1;
    searchInput?.setAttribute('aria-expanded', 'false');
    searchInput?.removeAttribute('aria-activedescendant');

    form?.classList.remove('predictive-search-form--loaded');
    const container = predictiveSearchResults.parentNode;
    container?.classList.toggle('predictive-search--no-results', noResults);

    const termDisplay = container?.querySelector('.js-search-term');
    const term = searchInput.value.trim();
    if (termDisplay) termDisplay.textContent = noResults ? `"${term}"` : '';

    predictiveSearchResults.innerHTML = this.noResultsTemplate.innerHTML;

    this.#renderHistory();
    await Promise.all([
      this.#renderTrending(),
      this.#renderTrendingProducts()
    ]);

    if (!this.isConnected) return;

    form?.classList.add('predictive-search-form--loaded');
  }

  #renderHistory() {
    const container = this.querySelector('#recent-searches');
    if (!container) return;
    container.innerHTML = this.#getSearchHistory().map(term => this.#renderSuggestionItem(term, '')).join('');
  }

  async #renderTrending() {
    const container = this.querySelector('#popular-searches');
    if (!container) return;
    try {
      const data = await this.apiRequest('trending');

      if (data?.trending?.queries) {
        container.replaceChildren(
          ...data.trending.queries.map(q => {
            const li = document.createElement('li');
            li.className = 'predictive-search-results__item';
            li.setAttribute('role', 'option');

            const a = document.createElement('a');
            a.href = localizeUrl(`/search?q=${encodeURIComponent(q.searchQuery)}`);
            a.className = 'predictive-search-results__link';

            const span = document.createElement('span');
            span.className = 'predictive-search-results__term';
            span.textContent = q.searchQuery;

            a.appendChild(span);
            li.appendChild(a);
            return li;
          })
        );
      }
    } catch (e) {}
  }

async #renderTrendingProducts() {
    const container = this.querySelector('#trending-products');
    const heading = this.querySelector('#trending-products-heading');

    if (!container) return;
    try {
      const data = await this.apiRequest('boost');

      if (data?.[0]?.results?.length > 0) {
        let trendingResults = data[0].results;

        // Extract variant IDs using the most robust logic possible
        const variantGids = trendingResults.map(p => {
          const core = p.mappings?.core || {};
          let allSwatches = [];
          
          try {
            allSwatches = core.ss_swatches ? JSON.parse(core.ss_swatches.replace(/&quot;/g, '"')) : [];
          } catch (e) {}

          const initialSwatch = allSwatches.find(s => s.available > 0) || {};
          
          let rawVariantId = core.variant_id || core.ss_id;
          if (Array.isArray(rawVariantId)) rawVariantId = rawVariantId[0];

          // 1. Try swatch URL first
          let vId = initialSwatch.url 
            ? new URL(initialSwatch.url, window.location.origin).searchParams.get('variant') 
            : rawVariantId;

          // 2. Fallback to extracting from the main product URL if variant_id is missing
          if (!vId && core.url) {
             try {
               vId = new URL(core.url, window.location.origin).searchParams.get('variant');
             } catch(e) {}
          }
          
          // 3. Absolute fallback to UID
          if (!vId) vId = core.uid;

          if (typeof vId === 'string' && vId.includes(',')) {
            vId = vId.split(',')[0].trim();
          }

          return `gid://shopify/ProductVariant/${vId}`;
        });

        // Fetch availability and price from Storefront API
        const { nodes: livePriceNodes } = await fetchPriceData(variantGids, {
          GRAPHQL_URL: this.GRAPHQL_URL,
          STOREFRONT_TOKEN: this.STOREFRONT_TOKEN
        });

        const priceMap = new Map();
        livePriceNodes.forEach(node => {
          if (node && node.id) priceMap.set(node.id, node);
        });

        // Filter trending products based on context
        trendingResults = trendingResults.filter((p, index) => {
          const core = p.mappings?.core;
          if (!core) return false;

          const vId = variantGids[index];
          const liveNode = priceMap.get(vId);

          // RELAXED CHECK: If liveNode is missing (due to bad Searchspring ID data), 
          // we fallback to Searchspring's native availability instead of hiding it.
          const is_product_available = (core.ss_available === '0' || (liveNode && liveNode.availableForSale === false)) ? false : true;

          if (!is_product_available) return false;

          // Attach live prices directly to the core mapping for the template
          if (liveNode) {
            core.livePrice = liveNode.price?.amount;
            core.liveCompareAt = liveNode.compareAtPrice?.amount;
          }

          return true;
        });

        // Render filtered products if any remain
        if (trendingResults.length > 0) {
          container.replaceChildren(this.#generateProductsHtml(trendingResults, true));
          if (heading) heading.hidden = false;
        } else {
          if (heading) heading.hidden = true;
        }
      } else {
        if (heading) heading.hidden = true;
      }
    } catch (e) {
      console.error("Error fetching trending products:", e);
    }
  }

  #handleModalFocus = (e) => {
    if (!e.target.closest('button, a, input') && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  #handleKeyboardShortcut = (e) => {
    if (e.metaKey && e.key === 'k') {
      e.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  #handleKeyboardNavigation = (e) => {
    const resultsList = this.refs.predictiveSearchResults?.querySelector('#predictive-search-results');
    if (!resultsList) return;

    // Only navigate through product cards, not search history or suggestions
    const items = resultsList.querySelectorAll('[role="option"]');
    if (items.length === 0) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      this.#activeIndex = (this.#activeIndex + 1) % items.length;
      this.#updateSelection(items);
    }
    else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      this.#activeIndex = (this.#activeIndex - 1 + items.length) % items.length;
      this.#updateSelection(items);
    }
    else if (e.key === 'Enter' && this.#activeIndex > -1) {
      e.preventDefault();
      const link = items[this.#activeIndex].querySelector('a');
      if (link) link.click();
    }
    else if (e.key === 'Escape') {
      this.#activeIndex = -1;
      this.#updateSelection(items);
      this.refs.searchInput?.focus();
    }
  };

  #updateSelection(items) {
    items.forEach((item, index) => {
      const isSelected = index === this.#activeIndex;
      item.classList.toggle('is-selected', isSelected);

      if (isSelected) {
        const itemId = `result-item-${index}`;
        item.id = itemId;

        // Add descriptive aria-label for screen readers
        const productTitle = item.querySelector('.resource-card__title')?.textContent?.trim();
        if (productTitle) {
          item.setAttribute('aria-label', `Selected: ${productTitle}`);
        }

        this.refs.searchInput.setAttribute('aria-activedescendant', itemId);
        item.scrollIntoView({ block: 'nearest' });
      } else {
        // Remove aria-label when not selected
        item.removeAttribute('aria-label');
      }
    });

    if (this.#activeIndex === -1) {
      this.refs.searchInput.removeAttribute('aria-activedescendant');
    }
  }

  #toggleResetButton(show) {
    this.refs.resetButton.hidden = !show;
  }

  #getOrSetCookie(name) {
    let value = document.cookie.split('; ').find(row => row.startsWith(`${name}=`))?.split('=')[1];
    if (!value) {
      value = crypto.randomUUID?.() || Math.random().toString(36).substring(2);
      document.cookie = `${name}=${value}; path=/; max-age=31536000`;
    }
    return value;
  }

  #saveSearchTerm(term) {
    if (!term || term.length < minSearchLength) return;
    let history = this.#getSearchHistory();
    history = [term, ...history.filter(t => t.toLowerCase() !== term.toLowerCase())].slice(0, maxHistoryTerms);
    localStorage.setItem(searchHistoryKey, JSON.stringify(history));
  }

  #getSearchHistory() {
    try { return JSON.parse(localStorage.getItem(searchHistoryKey)) || []; } catch { return []; }
  }

  #saveSearchFromUrl() {
    const query = new URLSearchParams(window.location.search).get('q');
    if (query) this.#saveSearchTerm(query.trim());
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}